import type { ReactElement } from 'react';
import type { Element } from 'html-react-parser';
import dayjs from 'src/helpers/dayjs';
import CompetitionsList from 'src/common/components/CompetitionsList';
import { IEdition } from 'src/interfaces/Edition';

export interface PlaceholderRenderContext {
  currentEdition?: IEdition;
  dateFormat?: string;
}

const COMPETITIONS_LIST_BARE = '{{CompetitionsList}}';

const COMPETITIONS_LIST_ID = 'competitionsList';

const competitionsListPattern = /\{\{\s*CompetitionsList\s*\}\}/gi;

const LEGACY_COMPETITIONS_LIST_WITH_WHEN =
  /\{\{\s*CompetitionsList(?:\s+when\s*=\s*["']?\w+["']?)?\s*\}\}/gi;

function renderCompetitionsList(_ctx: PlaceholderRenderContext): ReactElement | null {
  return <CompetitionsList />;
}

interface TextPlaceholderDef {
  token: string;
  replace: (ctx: PlaceholderRenderContext) => string | null;
}

const TEXT_PLACEHOLDERS: TextPlaceholderDef[] = [
  {
    token: '{{CurrentEditionDate}}',
    replace: ctx =>
      ctx.currentEdition && ctx.dateFormat
        ? dayjs(ctx.currentEdition.date).format(ctx.dateFormat)
        : null,
  },
  {
    token: '{{CurrentEditionSignupDeadline}}',
    replace: ctx =>
      ctx.currentEdition && ctx.dateFormat
        ? dayjs(ctx.currentEdition.signupDeadline).format(ctx.dateFormat)
        : null,
  },
];

interface ComponentPlaceholderDef {
  id: string;
  pattern: RegExp;
  toMarkerHtml: () => string;
  render: (ctx: PlaceholderRenderContext) => ReactElement | null;
}

const COMPONENT_PLACEHOLDERS: ComponentPlaceholderDef[] = [
  {
    id: COMPETITIONS_LIST_ID,
    pattern: competitionsListPattern,
    toMarkerHtml: () => `<div data-ttt-placeholder="${COMPETITIONS_LIST_ID}"></div>`,
    render: renderCompetitionsList,
  },
];

interface EditorInsertSnippet {
  i18nKey: string;
  wrapParagraph?: boolean;
  content: string;
}

export const EDITOR_INSERT_SNIPPETS: EditorInsertSnippet[] = [
  {
    i18nKey: 'editor.competitionsList',
    content: COMPETITIONS_LIST_BARE,
  },
  { i18nKey: 'editor.currentEditionDate', content: '{{CurrentEditionDate}}' },
  {
    i18nKey: 'editor.currentEditionSignupDeadline',
    content: '{{CurrentEditionSignupDeadline}}',
  },
  {
    i18nKey: 'editor.horizontalRule',
    content: '<hr data-ttt-spacing="medium" />',
  },
  {
    i18nKey: 'editor.horizontalRuleLarge',
    content: '<hr data-ttt-spacing="large" />',
  },
];

export function preprocessPlaceholders(html: string): string {
  let result = html.replace(LEGACY_COMPETITIONS_LIST_WITH_WHEN, COMPETITIONS_LIST_BARE);
  for (const def of COMPONENT_PLACEHOLDERS) {
    const pattern = new RegExp(def.pattern.source, def.pattern.flags);
    result = result.replace(pattern, () => def.toMarkerHtml());
  }
  return result;
}

export function applyTextPlaceholders(text: string, ctx: PlaceholderRenderContext): string {
  let result = text;
  for (const { token, replace } of TEXT_PLACEHOLDERS) {
    if (!result.includes(token)) continue;
    const value = replace(ctx);
    if (value !== null) {
      result = result.replaceAll(token, value);
    }
  }
  return result;
}

const LEGACY_COMPETITIONS_LIST_ATTR = 'data-ttt-competitions-list';

export function renderPlaceholderMarker(
  domNode: Element,
  ctx: PlaceholderRenderContext,
): ReactElement | null {
  const id =
    domNode.attribs?.['data-ttt-placeholder'] ??
    (domNode.attribs?.[LEGACY_COMPETITIONS_LIST_ATTR] !== undefined
      ? COMPETITIONS_LIST_ID
      : undefined);
  if (!id) return null;

  const def = COMPONENT_PLACEHOLDERS.find(p => p.id === id);
  if (!def) return null;

  return def.render(ctx);
}

export function renderPlaceholderInText(
  text: string,
  ctx: PlaceholderRenderContext,
): ReactElement | null {
  const normalized = text.replace(LEGACY_COMPETITIONS_LIST_WITH_WHEN, COMPETITIONS_LIST_BARE);
  for (const def of COMPONENT_PLACEHOLDERS) {
    const pattern = new RegExp(def.pattern.source, def.pattern.flags.replace('g', ''));
    if (pattern.test(normalized)) {
      return def.render(ctx);
    }
  }
  return null;
}
